import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import Promise from 'bluebird';
import hoistStatics from 'hoist-non-react-statics';
import selectn from 'selectn';
import uniqBy from 'lodash/uniqBy';
import {
    BAM_PAYWALL_IFRAME_ID,
    FORCE_REFRESH_TOKEN_RETRY_KEY
} from 'constants';
import WatchESPNWebPlayer from 'webapp/common/video-player';
import FeedsSelector from 'webapp/player/FeedsSelector';
import WatchPropTypes from 'common/PropTypes';
import { AdobePass, PlayerEvents, PlayerError, PlayerPlacement, Flags, AuthTypes, /* SourceType, */ Entitlements } from 'enums';
import {
    contentSelect,
    contentUnSelect,
    streamSelect,
    streamUnSelect,
    pickerStreamsUnSelect,
    closeModalMessage
} from 'common/actions/appActions';
import {
    createAndPerformStartAuthorizationAction,
    createAsyncGetMetadataAction,
    createAndPerformLogoutAction,
    createDTCPaywallAction,
    createCompletePreauthCheckAction
} from 'common/actions/authenticationActions';
import { forceTokenRefresh, logout } from 'common/actions/oneIdActions';
import bootstrapSelector from 'common/selectors/bootstrapSelector';
import { nielsenTrackingSelector, comscoreTrackingSelector } from 'common/selectors/analyticsSelectors';
import {
    apiEnvSelector,
    playerTechSelector,
    bypassTokenSelector,
    isDomesticSelector,
    editionSelector,
    intlAppSelector,
    locationSelector,
    playbackMetaSelector,
    newRelicTrackingSelector,
    hideNavSelector,
    playerOptionsSelector
} from 'common/selectors/appSelector';
import {
    autoPlaySelector,
    prerollConfigSelector,
    lastFeedsSelector,
    recommendMobileWebSelector,
    playerAdsConfigSelector,
    dssPlayerMutedSelector
} from 'common/selectors/playerSelector';
import initialEmbedSelector from 'common/selectors/initialEmbedSelector';
import onDeckContentSelector from 'common/selectors/onDeckContentSelector';
import pageSelector from 'common/selectors/pageSelector';
import selectedContentSelector from 'common/selectors/selectedContentSelector';
import selectedStreamSelector from 'common/selectors/selectedStreamSelector';
import playingStreamSelector from 'common/selectors/playingStreamSelector';
import pickerStreamsSelector from 'common/selectors/pickerStreamsSelector';
import providerSelector from 'common/selectors/providerSelector';
import {
    userAuthSelector,
    preauthorizedResourcesSelector,
    passOneIdTokenSelector
} from 'common/selectors/authenticationSelectors';
import { oneIdTokenSelector } from 'common/selectors/oneIdSelector';
import { persistentFlagsSelector } from 'common/selectors/flagsSelector';
import {
    authStateKnown,
    streamHasAuthType,
    streamHasDTCPackage,
    shallowCheckAuthentication,
    canPlayContent,
    PreAuthZ
} from 'lib/auth';
import { newRelicTrackEvent } from 'lib/api/newRelicApi';
import { isEmpty, getBamAuthConfig } from 'lib/util';
import { addQueryToUrl } from 'lib/format';
import { getOmnitureQuery, trackEspnPlayerOnAdSkipped } from 'lib/omniture';
import injectIntl, { intlShape } from 'common/injectIntl';
import { getAdobeDCRConfig, getPlayerOptions } from './util';
import {
    createContinuousPlayAction,
    createAutoEmbedTimerCompleteAction,
    createAutoplayUpdatedAction,
    createAuthorizationErrorAction,
    createStreamPlayingAction,
    createPlayerReadyAction,
    createProgramChangeAction,
    createPlayerEmbeddingAction,
    createPlayerEmbeddedAction,
    createFeedSelectAction
} from './actions';
import globalVideoElement from './globalVideoElement';
import { set, get, remove } from 'lib/util/localStorage';
import {
    openStreamPicker,
    selectStreamForAuthUsers
} from 'lib/streamPicker';
import { getAppLink } from 'lib/native-bridge/espnapp';
import { shieldApiKeySelector } from 'common/selectors/apiParamsSelector';

const { METADATA } = AdobePass;

// Begin Higher Order Component definition
function connectPlayer(WrappedComponent) {
    class ESPNPlayer extends Component {
        constructor(props) {
            super(props);
            this.state = {
                autoEmbedCountDown: this.props.initialAutoEmbedCountDown,
                deferPlayerInitialization: false, // if we have nothing to play yet
                deferInitialStreamSelect: false,
                initialized: false,
                initializedWithAuthZError: false,
                socialOpened: false,
                showFeedsSelector: false,
                feedsSelectorMessage: ''
            };
            this.onToggleAutoPlay = this.onToggleAutoPlay.bind(this);
            this.onContentLoaded = this.onContentLoaded.bind(this);
            this.onSocialOpened = this.onSocialOpened.bind(this);
            this.onSocialClosed = this.onSocialClosed.bind(this);
            this.onProgramChange = this.onProgramChange.bind(this);
            this.onPlaybackError = this.onPlaybackError.bind(this);
            this.onDrmError = this.onDrmError.bind(this);
            this.onBAMAuthrizeUser = this.onBAMAuthrizeUser.bind(this);
            this.onBAMFeedChange = this.onBAMFeedChange.bind(this);
            this.onBAMSettingsChanged = this.onBAMSettingsChanged.bind(this);
            this.onBAMMediaLoaded = this.onBAMMediaLoaded.bind(this);
            this.onBAMPlaybackError = this.onBAMPlaybackError.bind(this);
            this.onFeedClick = this.onFeedClick.bind(this);
            this.hasUserFeedSwitchedStream = this.hasUserFeedSwitchedStream.bind(this);
            this.playerReady = this.playerReady.bind(this);
            this.initializePlayer = this.initializePlayer.bind(this);
            this.handleClick = this.handleClick.bind(this);
            this.unregisterEvents = this.unregisterEvents.bind(this);
            this.registerEvents = this.registerEvents.bind(this);
            this.startAutoEmbedTimer = this.startAutoEmbedTimer.bind(this);
            this.unembedPlayer = this.unembedPlayer.bind(this);
            this.embedPlayer = this.embedPlayer.bind(this);
            this.throwError = this.throwError.bind(this);
            this.newRelicTrackingOnPlayer = this.newRelicTrackingOnPlayer.bind(this);
            this.onFeedCloseModal = this.onFeedCloseModal.bind(this);
            // reference to the BrightCove player
            this.player = null;
            // is the component mounted?
            this._isMounted = false;
            // unmounting if we detect a route chage
            this._isUnMounting = false;
            // hold a reference to the timer to clear later
            this._autoEmbedTimer = null;
            this._isAuthorizing = false;
        }

        componentDidMount() {
            this._isMounted = true;
            this.startAutoEmbedTimer();
            /**
              Initialize the player on mount if:
                1 - It's the player page
                2 - The player has already been primed
                3 - It's not a mobile device
            */
            if (
                (this.props.playerPlacement === PlayerPlacement.PLAYER) ||
                globalVideoElement.primed ||
                !this.props.isMobile
            ) {
                this.initializePlayer();
            }

            // comment out newrelic tracking until tracking is required for new stream picker
            // const { isNewrelicTrackingOn, selectedStream } = this.props;
            // if (isNewrelicTrackingOn && selectedStream.length) {
            //     // send newrelic track for selected stream that is presented to the users
            //     this.newRelicTrackingOnPlayer(selectedStream);
            // }
        }

        componentWillReceiveProps(nextProps) {
            if (this._isMounted === false || this._isUnMounting === true) {
                return;
            }

            const {
                lang,
                country,
                dispatch,
                selectedContent,
                selectedStream,
                playingStream,
                userAuth,
                onDeckContent,
                routing: { locationBeforeTransitions }
            } = this.props;
            const {
                selectedContent: nextSelectedContent,
                playingStream: nextPlayingStream,
                userAuth: nextUserAuth,
                onDeckContent: nextOnDeckContent,
                pickerStreams: nextPickerStreams,
                recommendMobileWeb: nextRecommendMobileWeb,
                routing: { locationBeforeTransitions: nextLocationBeforeTransitions },
                lastFeeds: nextLastFeeds,
                preauthorizedResources: nextPreauthorizedResources
            } = nextProps;

            // if the user is no longer authd for the playing content, unembed the player
            if (
                shallowCheckAuthentication(playingStream, userAuth, false) &&
                !shallowCheckAuthentication(nextPlayingStream, nextUserAuth, false)
            ) {
                this.unembedPlayer();
                return;
            }

            if (this.state.feedsSelectorMessage && selectedContent.id === nextSelectedContent.id) {
                return;
            }

            // player initialization is deferred if there's no selected content, and page api has not yet returned
            if (
                this.state.deferPlayerInitialization &&
                nextProps.scoringComplete &&
                !isEmpty(nextProps.initialEmbedContent)
            ) {
                this.setState({ deferPlayerInitialization: false });
                this.initializePlayer(nextProps);
            }

            /*
            * The following logic handles:
            * 1) Verifying a valid authentication state (i.e. 'mvpd')
            * 2) Checking if the call to playerReady() updated the state to deferInitialStreamSelect as a truthy value.
            * Occurs when the page has not fully loaded and will be caught in componentDidMount()
            * 3) If deferInitialStreamSelect is `false`, the logic verifies that the `selectedContent`
            * and `selectedStream` are both empty or both non-empty or the boolean initializedWithAuthZError
            * is `true` and the `selectedStream` is empty then it will proceed to select the preferred stream
            * 4) If content is 'vod', user can play
            */
            const isVod = nextSelectedContent.type === 'vod';
            if (
                (authStateKnown(nextUserAuth) || isVod || !nextUserAuth.length) &&
                (
                    this.state.deferInitialStreamSelect ||
                    (
                        (
                            (isEmpty(selectedContent) && isEmpty(selectedStream)) ||
                            (!isEmpty(selectedContent) && !isEmpty(selectedStream)) ||
                            (isEmpty(selectedStream) && (this.state.initializedWithAuthZError ||
                             isVod || !nextUserAuth.length))
                        ) &&
                        selectedContent.id !== nextSelectedContent.id // this is for the subsequent content change
                    )
                )
            ) {
                if (this.state.deferInitialStreamSelect) {
                    this.setState({ deferInitialStreamSelect: false });
                }

                /* Verifies that a user can play the selected content by:
                * 1) Ensuring the `selectedContent` status is not `upcoming`
                * 2) Ensuring that there is no program change in progress via `onProgramChange`
                */
                if (canPlayContent(nextSelectedContent, nextUserAuth, false) && !this.state.programChanging) {
                    const { links: { picker } = {}, isOnTile } = nextSelectedContent;
                    const { query: { eventCalendarId } = {}, pathname } = nextLocationBeforeTransitions;
                    const authRes = { userAuth: nextUserAuth, preauthorizedResources: nextPreauthorizedResources };

                    if (isOnTile && playingStream && pathname.indexOf('/player') === -1) {
                        /**
                         * when user is logged in and clicks on the tiles, we dispatch contentSelect in playable.js so
                         * that the new selected content will be updated and passed to stream picker; If it is on hero
                         * component and content is playing, we don't want to trigger new stream select.
                         */
                        return;
                    }

                    if (eventCalendarId && picker) {
                        openStreamPicker({
                            lang,
                            country,
                            dispatch,
                            authRes,
                            pickerStreams: nextPickerStreams,
                            content: nextSelectedContent,
                            route: { locationBeforeTransitions: nextLocationBeforeTransitions }
                        });
                    } else {
                        selectStreamForAuthUsers({
                            dispatch,
                            authRes,
                            pickerStreams: nextPickerStreams,
                            content: nextSelectedContent,
                            feeds: nextLastFeeds,
                            locationBeforeTransitions: nextLocationBeforeTransitions
                        });
                    }
                    if (this.state.feedsSelectorMessage) {
                        this.setState({ feedsSelectorMessage: '' });
                    }
                    return;
                }
            }

            // if the route has changed, anticipate the component unmounting
            if (locationBeforeTransitions.pathname !==
                nextLocationBeforeTransitions.pathname) {
                this._isUnMounting = true;
                this.unembedPlayer();
                return;
            }

            if (isEmpty(nextOnDeckContent) || (onDeckContent.id === nextOnDeckContent.id) ||
                nextProps.player.embedding === true || this._isAuthorizing === true) {
                return;
            }

            // NOTE: The logic below handles 1) embedding the player and commencing playback 2) error scenarios
            // Based on the selectedStream AuthType (BAM, MVPD/TVE, ISP) it will execute embedPlayer() with the
            // correct properties
            if (nextRecommendMobileWeb) {
                this.throwError(
                    nextOnDeckContent,
                    PlayerError.UNSUPPORTED,
                    'player.error.recommendMobileWeb',
                    nextProps
                );
            } else if (nextOnDeckContent.sourceId === 'SHORTSTOPLIVE') {
                this.embedPlayer(nextProps, nextOnDeckContent, { token: 'xbox_watch', tokenType: 'DEVICE' });
            } else if (nextProps.bypassToken) {
                this.embedPlayer(nextProps, nextOnDeckContent, {
                    token: decodeURIComponent(nextProps.bypassToken),
                    tokenType: 'GATEKEEPER',
                    encoded: true
                });
            } else if (!nextOnDeckContent.authTypes || nextOnDeckContent.authTypes.length === 0) {
                this.embedPlayer(nextProps, nextOnDeckContent);
            } else if (streamHasAuthType(nextOnDeckContent, AuthTypes.OPEN)) {
                this.embedPlayer(nextProps, nextOnDeckContent, { token: 'open', tokenType: 'OPEN' });
            } else if (streamHasAuthType(nextOnDeckContent, AuthTypes.ISP) && nextProps.userAuth.indexOf(AuthTypes.ISP) !== -1) {
                this.embedPlayer(nextProps, nextOnDeckContent, { token: 'online', tokenType: 'DEVICE' });
            } else if (streamHasAuthType(nextOnDeckContent, AuthTypes.MVPD)) {
                const adobePassInfo = nextOnDeckContent.adobePassInfo;
                if (adobePassInfo) {
                    const { messages, intl: { formatMessage }, preauthorizedResources } = nextProps;
                    this._isAuthorizing = true;
                    dispatch(createAndPerformStartAuthorizationAction(nextOnDeckContent.adobeRSS))
                        .then((authorization) => {
                            if (locationBeforeTransitions && locationBeforeTransitions.query &&
                                locationBeforeTransitions.query.prePlayPicker) {
                                return Promise.reject(new Error(formatMessage(messages['player.error.unexpectedMessage'])));
                            }
                            if (authorization.resourceId && authorization.token) {
                                // in case preauthz rsource info is incorrect, add the network to the list when
                                // the playback is successful
                                const { adobeRSS = '' } = nextOnDeckContent;
                                const result = /<title>([^<]+)<\/title>/.exec(adobeRSS);
                                if (result && result[1]) {
                                    const adobeResource = result[1];
                                    if (adobeResource) {
                                        if (preauthorizedResources && preauthorizedResources.indexOf(adobeResource) === -1) {
                                            const resources = preauthorizedResources.concat(adobeResource);
                                            PreAuthZ.setResources(resources, 1);
                                            dispatch(createCompletePreauthCheckAction(resources));
                                        }
                                    }
                                }
                                return dispatch(createAsyncGetMetadataAction(METADATA.UPSTREAM_USERID))
                                    .then(accountId => { // eslint-disable-line arrow-body-style
                                        // if accountId doesn't exist, use a random number to avoid stream limiting everyone
                                        return this.embedPlayer(nextProps, nextOnDeckContent, authorization, {
                                            mvpd: adobePassInfo.abbreviation,
                                            accountId
                                        });
                                    });
                            }
                            return Promise.reject(new Error(formatMessage(messages['player.error.unexpectedMessage'])));
                        })
                        .catch(({ message }) => {
                            const { playerPlacement, pickerStreams = [] } = this.props;
                            // On non-player page (i.e. hero), don't embed the player with an error
                            // If the user is on a hero player scenario and selects an additional feed
                            // from the feed selector, ensure error handling is in place only after
                            // the user has initiated the click event
                            if (
                                !this.state.initializedWithAuthZError &&
                                playerPlacement !== PlayerPlacement.PLAYER &&
                                !this.hasUserFeedSwitchedStream(nextOnDeckContent)
                            ) {
                                if (pickerStreams.length) {
                                    this.unregisterEvents();
                                    this.unembedPlayer();
                                    // TODO: localize message
                                    this.setState({
                                        showFeedsSelector: true,
                                        feedsSelectorMessage: message,
                                        initializedWithAuthZError: true
                                    });
                                } else {
                                // if the user clicks on the event, don't do this logic again
                                    this.setState({
                                        initializedWithAuthZError: true
                                    });
                                    dispatch(contentUnSelect());
                                    dispatch(streamUnSelect());
                                }
                                return;
                            }

                            dispatch(createAuthorizationErrorAction(message));
                            dispatch(createStreamPlayingAction({ stream: nextOnDeckContent, error: true }));

                            this.throwError(
                                nextOnDeckContent,
                                PlayerError.AUTHORIZATION,
                                message && message !== 'null' ? message : 'player.error.defaultMessage',
                                nextProps
                            );
                            // Fixes undhandled promise rejection error in console
                            return null;  // eslint-disable-line consistent-return
                        }).finally(() => {
                            this._isAuthorizing = false;
                        });
                }
            } else if (streamHasAuthType(nextOnDeckContent, AuthTypes.BAM)) {
                // TODO: DTC - need authorization
                this.embedPlayer(nextProps, nextOnDeckContent);
            }
        }

        shouldComponentUpdate() {
            return (this._isMounted === true && this._isUnMounting === false);
        }

        componentWillUnmount() {
            const { dispatch } = this.props;
            this._isMounted = false;
            clearTimeout(this._autoEmbedTimer);
            this.unregisterEvents();
            this.unembedPlayer(true);
            dispatch(pickerStreamsUnSelect());
        }

        onToggleAutoPlay(event, { autoplay = true }) {
            this.props.dispatch(createAutoplayUpdatedAction(autoplay));
        }

        onContentLoaded(event, { id, clipId, mediainfo = {} } = {}) {
            if (event.type === PlayerEvents.ESPN_CONTENT_LOADED) {
                // `clipId` is for domestic and `sourceId=intlvod` is for intl VOD
                if (clipId || mediainfo.sourceId === 'intlvod') {
                    this.props.dispatch(createContinuousPlayAction(clipId || id));
                } else {
                    const { dispatch, selectedStream } = this.props;
                    if (selectedStream.feedSwitch) {
                        dispatch(createFeedSelectAction(id));
                        delete selectedStream.feedSwitch;
                    }
                }
                // update nielsen tracking info with the new content
                this.videoPlayer.getPlayer(WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE).analytics.configure({
                    adobeDCR: getAdobeDCRConfig(this.props.nielsenTrackingData, {}, this.props.env, 'brightcove')
                });
            }
        }

        onSocialOpened() {
            this.setState({ socialOpened: true });
            if (this.props.onSocialOpened) {
                this.props.onSocialOpened();
            }
        }

        onSocialClosed() {
            this.setState({ socialOpened: false });
            if (this.props.onSocialClosed) {
                this.props.onSocialClosed();
            }
        }

        onCaptionOpened() {
            this.setState({ captionOpened: true });
            if (this.props.onCaptionOpened) {
                this.props.onCaptionOpened();
            }
        }

        onCaptionClosed() {
            this.setState({ captionOpened: false });
            if (this.props.onCaptionClosed) {
                this.props.onCaptionClosed();
            }
        }

        onBAMAuthrizeUser(event) { // eslint-disable-line
            const { success } = event;
            if (!success) {
                console.error('onBAMAuthrizeUser', event);
                const { dispatch, selectedContent, selectedStream, oneIdToken } = this.props;
                // when there is a token and still trigger an auth error, consider it's token expiration
                if (oneIdToken.id_token) {
                    let retryCount = get(FORCE_REFRESH_TOKEN_RETRY_KEY);
                    if (retryCount) {
                        retryCount = parseInt(retryCount, 10);
                        if (retryCount >= 1) {
                            remove(FORCE_REFRESH_TOKEN_RETRY_KEY, retryCount);
                            dispatch(logout());
                            return;
                        }
                        retryCount += 1;
                    } else {
                        retryCount = 1;
                    }
                    set(FORCE_REFRESH_TOKEN_RETRY_KEY, retryCount);
                    dispatch(forceTokenRefresh())
                        .then(() => {
                            window.location.reload();
                        })
                        .catch(err => {
                            console.error(err);
                            dispatch(logout());
                        });
                    return;
                }
                dispatch(createDTCPaywallAction({
                    containerId: BAM_PAYWALL_IFRAME_ID,
                    content: selectedContent,
                    stream: selectedStream
                }));
            }
        }

        onBAMFeedChange(event) {
            console.log('onBAMFeedChange', event); // eslint-disable-line no-console
            const { body: { stream } = {} } = event;
            if (stream) {
                const { dispatch, selectedContent } = this.props;
                dispatch(contentSelect({ ...selectedContent, reEmbed: true }));
                // TODO: set this in the event when BAM player fires successful playback event
                if (streamHasAuthType(stream, AuthTypes.BAM)) {
                    dispatch(createFeedSelectAction(stream.id));
                }
                stream.feedSwitch = true;
                dispatch(streamSelect(stream));
            }
        }

        onBAMSettingsChanged(event) { // eslint-disable-line
            console.log('onBAMSettingsChanged', event); // eslint-disable-line no-console
            // TODO: implement logic to reflect settings change from BAM player to BC player
        }

        onBAMMediaLoaded(event) { // eslint-disable-line
            console.log('onBAMMediaLoaded', event); // eslint-disable-line no-console
        }

        onBAMPlaybackError(event) { // eslint-disable-line
            console.log('onBAMPlaybackError', event); // eslint-disable-line no-console
            const { exception: { message = '' } = {} } = event;
            const { dispatch, selectedContent, selectedStream, pickerStreams = [] } = this.props;
            if (message === 'NotEntitled') {
                dispatch(createDTCPaywallAction({
                    containerId: BAM_PAYWALL_IFRAME_ID,
                    content: selectedContent,
                    stream: selectedStream
                }));
            } else {
                const isBlackout = message.toLowerCase().indexOf('blackout') > -1;
                const isStreamConcurrencyViolation = message.toLowerCase().indexOf('streamconcurrencyviolation') > -1;
                let feedsSelectorMessage = message;

                if (isBlackout) {
                    selectedStream.blackoutText = 'BLACKOUT';
                    feedsSelectorMessage = 'This event has been blacked out in your area.';
                } else if (isStreamConcurrencyViolation) {
                    feedsSelectorMessage = `You have reached your device limit for streaming.
                     To continue viewing on this device, please stop streaming on another device.`;
                }

                const { streams = [] } = selectedContent;

                // on first embed, embed with an error
                if (streams.length > 1 || pickerStreams.length) {
                    this.unregisterEvents();
                    this.unembedPlayer();
                    // TODO: localize message
                    this.setState({
                        showFeedsSelector: true,
                        feedsSelectorMessage
                    });
                }
            }
        }

        onProgramChange(event, { id, eventId, callback }) {
            if (event.type === PlayerEvents.ESPN_PROGRAM_CHANGE) {
                // guard against program change called with the same id
                const { playingStream = {} } = this.props;
                if (playingStream.id === id) {
                    return;
                }
                this.setState({ programChanging: true });
                const query = eventId ? { eventId } : { id };
                this.props.dispatch(createProgramChangeAction(query, id)).then((trackingData) => {
                    this.setState({ programChanging: false });
                    // update nielsen tracking info with the new
                    this.videoPlayer.getPlayer(WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE).analytics.configure({
                        adobeDCR: getAdobeDCRConfig(
                            trackingData || this.props.nielsenTrackingData,
                            { startType: 'live-show-boundary' },
                            this.props.env,
                            'brightcove'
                        )
                    });
                    // this actually triggers the analytics call for program changes
                    if (callback && typeof callback === 'function') {
                        callback();
                    }
                });
            }
        }

        onPlaybackError(event, error) {
            console.log('onPlaybackError', error); // eslint-disable-line no-console
            if (error) {
                let customErrorMessage;
                const { selectedStream, selectedContent: { streams = [] }, pickerStreams = [] } = this.props;
                if ((error.code === 'BLACKOUT' || error.message.indexOf('blacked out') > -1)) {
                    selectedStream.blackoutText = 'BLACKOUT';
                }

                if (
                    (error.code === 403 || (error.type && error.type.indexOf('authorization'))) &&
                    typeof error.message === 'string' &&
                    error.message.trim() === ''
                ) {
                    customErrorMessage = 'You are not authorized to view this event';
                }

                // on first embed, embed with an error
                if (streams.length > 1 || pickerStreams.length) {
                    this.unregisterEvents();
                    this.unembedPlayer();
                    this.setState({
                        showFeedsSelector: true,
                        feedsSelectorMessage: customErrorMessage || error.message
                    });
                }
            }
        }

        onDrmError(event, error) {
            console.log('onDrmError', error); // eslint-disable-line no-console

            const { selectedStream, appName, hideNav, apiEnv, flags } = this.props;
            const isEditions = (appName === 'watcheditions' || appName === 'webapp') && hideNav;
            const appDrmSupport = flags[Flags.APP_DRM_SUPPORT];
            const appSupportsDrm = appDrmSupport !== null && appDrmSupport !== undefined;

            if (!isEditions || !selectedStream || !appSupportsDrm) {
                return;
            }

            // target url is:
            // watchespn-editions://x-callback-url/openBrowser?url=https://www.espn.com/watch/player?id=1d405c1b-481a-4804-9f21-4595a0073a72

            let url = `https://${window.location.host}/watch/player?id=${selectedStream.id}&roadblock=false`;
            if (apiEnv) {
                url = `${url}&apienv=${apiEnv}`;
            }
            const params = {
                url: encodeURIComponent(url)
            };

            let appLink = getAppLink('openBrowser', params, false);
            appLink = appLink.replace('sportscenter://', 'watchespn-editions://');

            window.location = appLink;
        }

        onFeedClick(feed) {
            const { dispatch, selectedContent } = this.props;
            const { streams = [] } = feed;
            let feedStream = feed;
            let feedContent = selectedContent;

            if (streams && streams.length) {
                feedStream = streams[0];
                feedContent = feed;
            }

            if (feedStream) {
                this.setState({
                    feedsSelectorMessage: ''
                });

                dispatch(contentSelect({ ...feedContent, reEmbed: true }));
                dispatch(streamUnSelect());
                dispatch(closeModalMessage());
                feedStream.feedSwitch = true;
                dispatch(streamSelect(feedStream));
            }
        }

        onFeedCloseModal() {
            this.setState({ showFeedsSelector: false });
        }

        getDSSPlayerMutedOnAuthPlay(mutedQuery, authPlay) {
            if (authPlay === true || authPlay === 'true') {
                const { dssPlayerMuted } = this.props;
                let muted = dssPlayerMuted;
                if (mutedQuery !== undefined) {
                    if (mutedQuery === false || mutedQuery === 'false') {
                        muted = false;
                    } else if (mutedQuery === true || mutedQuery === 'true') {
                        muted = true;
                    }
                }
                return muted;
            }
            return null;
        }

        /**
         * hasUserFeedSwitchedStream - Handles returning a boolean flag which determines
         if the
         * current stream has been triggered via a feedSwitch (i.e. click from feed selector dialog)
         *
         * @param {Object} currentStream - Stream object containing the feedSwitch property
         *
         * @return {Boolean}
         */
        hasUserFeedSwitchedStream(currentStream) { // eslint-disable-line class-methods-use-this
            return currentStream && currentStream.feedSwitch ? currentStream.feedSwitch : false;
        }

        // fire actions and set state to indicate the player is ready for embed
        playerReady(initialEmbedContent = null) {
            const {
                disableAuthPrompt,
                dispatch,
                placement,
                bucketId,
                bucketName,
                pageId,
                pageName,
                playerPlacement,
                userAuth,
                lastFeeds,
                routing: { locationBeforeTransitions }
            } = this.props;
            const { query = {}, pathname } = locationBeforeTransitions || {};
            const playerReadyAction = dispatch(
                createPlayerReadyAction(playerPlacement, initialEmbedContent)
            );

            const omnitureQuery = getOmnitureQuery(query);

            if (playerReadyAction.autoEmbed === true) {
                dispatch(contentSelect({
                    ...initialEmbedContent,
                    disableAuthPrompt,
                    placement,
                    bucketId,
                    bucketName,
                    pageId,
                    pageName,
                    ...omnitureQuery
                }));

                const { preauthorizedResources, pickerStreams } = this.props;
                const authRes = { userAuth, preauthorizedResources };
                const isVod = initialEmbedContent && initialEmbedContent.type === 'vod';

                if (authStateKnown(userAuth) || isVod || !userAuth.length) {
                    if (canPlayContent(initialEmbedContent, userAuth, false)) {
                        selectStreamForAuthUsers({
                            dispatch,
                            authRes,
                            pickerStreams,
                            content: initialEmbedContent,
                            feeds: lastFeeds,
                            locationBeforeTransitions
                        });
                    }
                } else {
                    /**
                     * when user is not authenticated. We don't want to trigger auth flow
                     * for non-player pages
                     */
                    if (pathname.indexOf('/player') === -1 && initialEmbedContent.id === query.id) {
                        dispatch(contentSelect(initialEmbedContent));
                    }
                    this.setState({ deferInitialStreamSelect: true });
                }
            }
        }

        async newRelicTrackingOnPlayer(stream) {
            const {
                env,
                device,
                newrelicInsertKey,
                newrelicAccountId,
                oneId
             } = this.props;
            const { id, content, streams } = stream;
            const param = {
                newrelicInsertKey,
                newrelicAccountId,
                trackingData: {
                    env,
                    device,
                    eventId: content ? content.eventId : null,
                    action: 'SelectedStreamOnPlayer',
                    streams: id,
                    streamCount: streams ? streams.length : 1,
                    swid: oneId.swid,
                    eventType: 'PlaybackFlow',
                    devicePlatform: 'watchWeb'
                }
            };

            await newRelicTrackEvent(param);
        }

        // called on componentDidMount (if autoplay), handleClick, or componentWillReceiveProps for deferred initialization.
        // takes props as a parameter for when called from componentWillReceiveProps, which passes "nextProps" in
        initializePlayer(nextProps) {
            const props = nextProps || this.props;
            if (this.state.initialized === false) {
                if (this._isMounted === false) {
                    return;
                }

                const {
                    bootstrap: { bam },
                    initialEmbedContent,
                    scoringComplete,
                    playerTech,
                    routing: { locationBeforeTransitions }
                } = props;
                const { query = {} } = locationBeforeTransitions || {};

                // if there is no content yet (no selected event and page request hasn't completed),
                // defer player initialization to componentWillReceiveProps
                if ((!initialEmbedContent || isEmpty(initialEmbedContent)) || !scoringComplete) {
                    this.setState({ deferPlayerInitialization: true });
                    return;
                }

                this.videoPlayer = new WatchESPNWebPlayer({
                    bamPlayerOptions: query[Flags.BAM_ENV] ? { ...bam, env: query[Flags.BAM_ENV] } : bam,
                    brightcovePlayerOptions: {
                        playerTech,
                        optimizeLive: true,
                        // Enables the Brightcove player to use the native HTML5 video player when set to true
                        // The `native` value is passed via query parameter (playertech=native)
                        native: (playerTech === 'native')
                    }
                });
                this.playerReady(initialEmbedContent);
                this.setState({ initialized: true });
            }
        }

        handleClick(event) {
            event.preventDefault();
            globalVideoElement.prime();

            if (this.props.clickHandler) {
                this.props.clickHandler(event);
            }

            // only initialize player if it hasn't been already
            if (this.props.player.embedded === false) {
                this.initializePlayer(this.props);
            }
        }

        unregisterEvents() {
            if (!this.videoPlayer) {
                return;
            }
            // Brightcove Player Events
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_AUTOPLAY_TOGGLED, this.onToggleAutoPlay);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_PROGRAM_CHANGE, this.onProgramChange);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_CONTENT_LOADED, this.onContentLoaded);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_SOCIAL_OPENED, this.onSocialOpened);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_SOCIAL_CLOSED, this.onSocialClosed);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_CAPTION_OPENED, this.onCaptionOpened);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_CAPTION_CLOSED, this.onCaptionClosed);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_ERROR, this.onPlaybackError);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_DRM_ERROR, this.onDrmError);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.ESPN_ADS_AD_SKIPPED, trackEspnPlayerOnAdSkipped);

            // BAM Player Events
            this.videoPlayer.off(WatchESPNWebPlayer.Events.AUTHORIZE_USER, this.onBAMAuthrizeUser);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.FEED_CHANGE_REQUEST, this.onBAMFeedChange);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.SETTINGS_CHANGED, this.onBAMSettingsChanged);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.MEDIA_LOADED, this.onBAMMediaLoaded);
            this.videoPlayer.off(WatchESPNWebPlayer.Events.PLAYBACK_ERROR, this.onBAMPlaybackError);
        }

        registerEvents() {
            if (!this.videoPlayer) {
                return;
            }
            // Brightcove Player Events
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_AUTOPLAY_TOGGLED, this.onToggleAutoPlay);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_PROGRAM_CHANGE, this.onProgramChange);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_CONTENT_LOADED, this.onContentLoaded);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_SOCIAL_OPENED, this.onSocialOpened);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_SOCIAL_CLOSED, this.onSocialClosed);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_CAPTION_OPENED, this.onCaptionOpened);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_CAPTION_CLOSED, this.onCaptionClosed);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_ERROR, this.onPlaybackError);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_DRM_ERROR, this.onDrmError);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.ESPN_ADS_AD_SKIPPED, trackEspnPlayerOnAdSkipped);

            // BAM Player Events
            this.videoPlayer.on(WatchESPNWebPlayer.Events.AUTHORIZE_USER, this.onBAMAuthrizeUser);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.FEED_CHANGE_REQUEST, this.onBAMFeedChange);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.SETTINGS_CHANGED, this.onBAMSettingsChanged);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.MEDIA_LOADED, this.onBAMMediaLoaded);
            this.videoPlayer.on(WatchESPNWebPlayer.Events.PLAYBACK_ERROR, this.onBAMPlaybackError);
        }

        startAutoEmbedTimer() {
            if (this.state.autoEmbedCountDown <= 0) {
                this.props.dispatch(createAutoEmbedTimerCompleteAction());
                clearTimeout(this._autoEmbedTimer);
                return;
            }
            if (this._isMounted === true && this.state.autoEmbedCountDown > 0) {
                this.setState({ autoEmbedCountDown: this.state.autoEmbedCountDown - 1 });
                this._autoEmbedTimer = setTimeout(this.startAutoEmbedTimer, 1000);
            }
        }

        /**
         * Display an internationalized error message within the player.
         * Embeds with an error if not embedded, otherwise calls error on the player object.
         * @param {object} stream
         * @param {string} type
         * @param {string} messageKey
         * @param {object} props
         */
        throwError(stream, type = PlayerError.DEFAULT, messageArg, props = this.props) {
            const {
                messages,
                intl: { formatMessage },
                player,
                selectedContent,
                routing: { locationBeforeTransitions }
            } = props;
            const { query = {} } = locationBeforeTransitions || {};
            let message = messageArg;
            if (message.indexOf('player.error') === 0) {
                message = formatMessage(messages[message]);
            }

            const errorObj = {
                type,
                message
            };
            let { streams = [] } = selectedContent;
            // ensure to remove dupes, avoids FeedsSelector component from re-rendering duplicate streams
            streams = streams.length && uniqBy(streams, 'id');

            if (query.prePlayPicker && streams.length === 1) {
                streams.push({ ...streams[0], tier: 'primary', id: `${streams[0].id}1`, test: true });
                streams.push({ ...streams[0], tier: 'primary', id: `${streams[0].id}2`, test: true });
                streams.push({ ...streams[0], tier: 'secondary', id: `${streams[0].id}3`, test: true });
            }

            // on first embed, embed with an error
            if (streams.length > 1) {
                this.unregisterEvents();
                this.unembedPlayer();
                this.setState({
                    showFeedsSelector: true,
                    feedsSelectorMessage: message
                });
            } else if (player.embedded === false) {
                this.embedPlayer(props, stream, {}, {}, errorObj);
            } else {
                this.videoPlayer.error(errorObj);
            }
        }

        async unembedPlayer(destroy) {
            const { dispatch } = this.props;
            if (this.videoPlayer) {
                // removes flickering of previous thumbnail
                globalVideoElement.poster = '';
                try {
                    if (destroy) {
                        await this.videoPlayer.destroy();
                    } else {
                        await this.videoPlayer.delete();
                    }
                } catch (err) {
                    console.error(err);
                }
            }
            dispatch(createStreamPlayingAction({ stream: {} }));
            dispatch(createPlayerEmbeddedAction(false));
        }

        createSettingsMenuConfig(stream, props = this.props) {
            const {
                dispatch,
                isDomestic,
                provider,
                userAuth,
                messages,
                intl: { formatMessage }
            } = props;
            const menuItems = [];

            if (
                isDomestic &&
                streamHasAuthType(stream, AuthTypes.MVPD) &&
                userAuth.indexOf(AuthTypes.MVPD) > -1 &&
                provider.name
            ) {
                menuItems.push({
                    label: formatMessage(messages['player.menu.logout'], { providerName: provider.name }),
                    fn: () => {
                        dispatch(createAndPerformLogoutAction(this.props.onDeckContent));
                    }
                });
            }

            return {
                menuItems
            };
        }

        createLogoConfig(stream, props = this.props) {
            const {
                bootstrap: { player: { logos = {} } = {} },
                isDomestic,
                provider,
                routing: { locationBeforeTransitions },
                flags,
                location: { origin }
            } = props;
            const { pathname } = locationBeforeTransitions || {};
            let logo;
            if (!isDomestic || pathname === '/syndicated/player') { // syndicated player has its header to show logo
                return logo;
            }
            if (streamHasAuthType(stream, AuthTypes.MVPD) || streamHasAuthType(stream, AuthTypes.ISP)) {
                logo = {
                    src: selectn('images.colorWithNegativeBackground.href', provider) ||
                    selectn('images.logo.href', provider),
                    href: selectn('links.web.href', provider)
                };
            } else if (streamHasAuthType(stream, AuthTypes.BAM)) {
                if (streamHasDTCPackage(stream, Entitlements.ESPN_PLUS_NHL)) {
                    logo = logos[Entitlements.ESPN_PLUS_NHL];
                } else if (streamHasDTCPackage(stream, Entitlements.ESPN_PLUS_MLB)) {
                    logo = logos[Entitlements.ESPN_PLUS_MLB];
                } else if (streamHasDTCPackage(stream, Entitlements.ESPN_PLUS)) {
                    logo = logos[Entitlements.ESPN_PLUS];
                }
            }
            if (logo && logo.href && logo.href.indexOf('http') !== 0) {
                logo.href = `${addQueryToUrl(`${origin}${logo.href}`, flags)}`;
            }
            return logo;
        }

        createFeedsMenuConfig(stream, props = this.props) {
            const { feedsSelectorMessage } = this.state;
            const {
                messages,
                intl: { formatMessage },
                pickerStreams
            } = props;

            const feeds = (pickerStreams.length > 0) && pickerStreams.map(streamItem => {
                const streamData = { ...streamItem };
                if (Array.isArray(streamData.contents) && streamData.contents.length > 0) {
                    streamData.contents.forEach(item => {
                        const selected = (item && typeof item.id === 'string' && item.id === stream.id);

                        item.selected = selected;
                        item.playbackErrorMessage = selected && feedsSelectorMessage;
                    });
                }

                return {
                    ...streamData,
                    fn: (feed) => {
                        this.onFeedClick(feed);
                    }
                };
            });

            const feedsMenu = pickerStreams.length > 0 && {
                title: formatMessage(messages['player.feeds.title']),
                feeds
            };
            return feedsMenu;
        }

        async embedPlayer(props = this.props, stream, authorization = {}, streamlimiter = {}, error = null, callback = () => {}) {
            // the playerContainer is the container in which we embed the player
            // (its DOM is not manipulated by the ESPNVideoPlayer)
            const { wrappedComponent: { refs: wrappedComponentRefs = {} } = {} } = this.refs || {};
            const { playerContainer } = wrappedComponentRefs;

            if (playerContainer) {
                this.setState({
                    showFeedsSelector: false,
                    feedsSelectorMessage: ''
                });

                const {
                    apiEnv,
                    env,
                    lang,
                    nielsenTrackingData,
                    comscoreTrackingData,
                    dispatch,
                    isMobile,
                    device,
                    provider,
                    isDomestic,
                    edition,
                    autoPlay,
                    intlApp,
                    oneIdToken,
                    passOneIdToken,
                    selectedContent,
                    userInitiated,
                    location,
                    routing: { locationBeforeTransitions },
                    playbackMeta,
                    prerollConfig,
                    playerAdsConfig,
                    userAuth,
                    pickerStreams,
                    shieldApiKey,
                    playerOpts
                } = props;
                const { query = {} } = locationBeforeTransitions || {};
                const { query: locationQueryParams, host, protocol } = location || {};
                const auth = {
                    ...authorization,
                    partnerName: query[Flags.PARTNER]
                };
                const social = !intlApp ? {
                    url: 'http://www.espn.com/watch?id={{ID}}'
                } : false;
                const playerTech = streamHasAuthType(stream, AuthTypes.BAM) ?
                    WatchESPNWebPlayer.PlayerTech.BAM : WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE;
                let currentPlayerTech;

                if (this.videoPlayer) {
                    const player = this.videoPlayer.getPlayer();
                    if (player && player.tech === WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE) {
                        currentPlayerTech = WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE;
                    } else if (player && player.tech === WatchESPNWebPlayer.PlayerTech.BAM) {
                        currentPlayerTech = WatchESPNWebPlayer.PlayerTech.BAM;
                    }
                }

                try {
                    if (currentPlayerTech === WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE) {
                        // always destroy Brightcove player
                        await this.videoPlayer.delete();
                    } else if (
                        currentPlayerTech === WatchESPNWebPlayer.PlayerTech.BAM &&
                        playerTech === WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE
                    ) {
                        // once BAM player is created, let's not destroy it but just deactivate
                        // await this.videoPlayer.deactivate();
                        await this.videoPlayer.delete();
                    }
                } catch (err) {
                    console.error(err);
                }

                // create `logo`
                const logo = this.createLogoConfig(stream, props);
                // create `settingsMenu`
                const settingsMenu = this.createSettingsMenuConfig(stream, props);
                // create `feedsMenu`
                const feedsMenu = this.createFeedsMenuConfig(stream, props);

                const opts = getPlayerOptions({
                    apiEnv,
                    stream,
                    authorization: auth,
                    streamlimiter,
                    nielsenTrackingData,
                    comscoreTrackingData,
                    isMobile,
                    device,
                    error,
                    env,
                    lang,
                    provider,
                    isDomestic,
                    social,
                    userInitiated,
                    logo,
                    settingsMenu,
                    feedsMenu,
                    playbackMeta,
                    prerollConfig,
                    adsConfig: playerAdsConfig,
                    edition,
                    userAuth,
                    pickerStreams,
                    shieldApiKey,
                    playerOpts
                });
                console.log('ESPN PLAYER FEEDS MENU:', feedsMenu); // eslint-disable-line
                console.log('ESPN PLAYER OPTIONS:', opts); // eslint-disable-line

                // `autoPlay` flag only applies to the initial playback.
                const autoplay = autoPlay || !!globalVideoElement.primed;
                const isBamPlayer = playerTech === WatchESPNWebPlayer.PlayerTech.BAM;
                const isBrightCovePlayer = playerTech === WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE;

                dispatch(createPlayerEmbeddingAction(true));

                if (isBrightCovePlayer) {
                    globalVideoElement.poster = stream.content.imageHref;
                    globalVideoElement.className = 'vjs-tech';
                }

                const elementId = isBrightCovePlayer ? 'watchespn-player' : 'watchespn-bam-player';
                let wrapperEl;

                if (
                    currentPlayerTech !== WatchESPNWebPlayer.PlayerTech.BAM ||
                    playerTech !== WatchESPNWebPlayer.PlayerTech.BAM
                ) {
                    // create a wrapper element so the video player doesn't manipulate the DOM that is registered with React
                    wrapperEl = document.createElement('div');
                    wrapperEl.setAttribute('data-vjs-player', '');
                    wrapperEl.id = elementId;
                    wrapperEl.style.display = 'none'; // to avoid FOUC
                    if (playerTech === WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE) {
                        wrapperEl.appendChild(globalVideoElement);
                    }

                    playerContainer.appendChild(wrapperEl);
                    wrapperEl.style.display = 'block';
                }

                let playerOptions = {
                    playerTech,
                    container: isBamPlayer ? `#${elementId}` : globalVideoElement
                };

                if (isBamPlayer) {
                    // test stream url: `ml=https://qa-espn-edge.bamgrid.com/media/052c5f81-4a57-4b9b-8d51-080c3730fabf/scenarios/{scenario}`
                    const streamObj = {
                        id: stream.id
                    };
                    // Derive the root url from react-router; default to production base url
                    const shareBaseUrl = (host && protocol) ? `${protocol}//${host}/watch/player` : 'http://www.espn.com/watch/player';
                    streamObj.shareUrl = `${shareBaseUrl}?id=${stream.id}`;

                    if (query.ml) { // test param
                        streamObj.mediaLocation = query.ml;
                    }

                    // Handles query parameter `t` for video startTime
                    if (query.t) {
                        streamObj.startTime = query.t;
                    } else if (locationQueryParams.t) {
                        streamObj.startTime = locationQueryParams.t;
                        delete locationQueryParams.t; // Prevent `t` from showing up in subsequent media loads
                    }

                    const muted = this.getDSSPlayerMutedOnAuthPlay(query[Flags.MUTED], autoPlay);
                    let customProps = {
                        autoPlay: autoplay,
                        muted,
                        analytics: opts.analytics,
                        poster: stream.content.imageHref,
                        logo,
                        adsConfig: playerAdsConfig, // Pass Active Ad Provider config derived from selector to BAM player
                        hudson: true
                    };

                    if (stream.feedSwitch && currentPlayerTech === WatchESPNWebPlayer.PlayerTech.BAM) {
                        customProps = {
                            ...customProps,
                            playlistItem: stream
                        };
                    } else {
                        customProps = {
                            ...customProps,
                            pageApiMeta: {
                                ...selectedContent,
                                ...{ pickerStreams }
                            }
                        };
                    }

                    playerOptions = {
                        ...playerOptions,
                        ...getBamAuthConfig(oneIdToken, passOneIdToken, userAuth),
                        language: {
                            ui: 'en',
                            audio: 'en'
                        },
                        playlist: [streamObj],
                        autoPlay: autoplay,
                        customProps
                    };
                } else if (isBrightCovePlayer) {
                    playerOptions = {
                        ...playerOptions,
                        options: { ...opts, autoplay }
                    };
                }

                try {
                    if (
                        stream.feedSwitch &&
                        currentPlayerTech === WatchESPNWebPlayer.PlayerTech.BAM &&
                        playerTech === WatchESPNWebPlayer.PlayerTech.BAM
                    ) {
                        delete stream.feedSwitch;
                        await this.videoPlayer.changeFeed(playerOptions);
                    } else {
                        await this.videoPlayer.create(playerOptions);
                    }
                } catch (err) {
                    console.error(err);
                }

                dispatch(createPlayerEmbeddingAction(false));
                if (this._isMounted === false || this._isUnMounting === true) {
                    this.videoPlayer.delete();
                    return;
                }

                if (wrapperEl) {
                    wrapperEl.style.display = 'block';
                }
                this.unregisterEvents();
                this.registerEvents();

                dispatch(createPlayerEmbeddedAction(true));
                dispatch(createStreamPlayingAction({ stream, error: !!error }));
                callback();
                // comment out newrelic tracking until tracking is required for new stream picker
                // if (isNewrelicTrackingOn &&
                //     (currentPlayerTech === WatchESPNWebPlayer.PlayerTech.BAM ||
                //         playerTech === WatchESPNWebPlayer.PlayerTech.BAM)) {
                //     // send newrelic track for selected stream that is presented to the users
                //     await this.newRelicTrackingOnPlayer(stream);
                // }
            } else {
                console.error(`Cannot embed player.  ${WrappedComponent.displayName} is missing "playerElement" ref`); // eslint-disable-line
            }
        }

        render() {
            const { showFeedsSelector } = this.state;
            // const showFeedsSelector = true;
            const { selectedStream, userAuth } = this.props;
            let feedsMenu;
            if (showFeedsSelector) {
                feedsMenu = this.createFeedsMenuConfig(selectedStream, this.props, true);
            }
            return (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <WrappedComponent
                        ref="wrappedComponent"
                        {...this.props}
                        {...this.state}
                        clickHandler={this.handleClick}
                    />
                    {showFeedsSelector && feedsMenu && (
                        <FeedsSelector
                            title={feedsMenu.title}
                            feeds={feedsMenu.feeds}
                            userAuth={userAuth}
                            onClick={this.onFeedClick}
                            onClose={this.onFeedCloseModal}
                        />
                    )}
                </div>
            );
        }
    }

    ESPNPlayer.propTypes = {
        bootstrap: WatchPropTypes.bootstrap,
        apiEnv: PropTypes.string,
        clickHandler: PropTypes.func,
        onSocialOpened: PropTypes.func,
        onSocialClosed: PropTypes.func,
        onCaptionOpened: PropTypes.func,
        onCaptionClosed: PropTypes.func,
        disableAuthPrompt: PropTypes.bool,            // ability to turn off auth prompt when selecting content
        dispatch: PropTypes.func,
        env: PropTypes.string,
        lang: PropTypes.string,
        initialAutoEmbedCountDown: PropTypes.number,  // time until autoplay
        initialEmbedContent: PropTypes.object,        // from selector, prefer selectedContent, fallback to hero,
        intl: intlShape.isRequired,
        isMobile: PropTypes.bool,                     // eslint-disable-line react/no-unused-prop-types
        messages: PropTypes.object.isRequired,
        nielsenTrackingData: PropTypes.object,        // from selector
        comscoreTrackingData: PropTypes.object,       // from selector
        onDeckContent: PropTypes.object,              // from selector, the content to be played next ()
        playerPlacement: PropTypes.string,            // placement of the actual player
        placement: PropTypes.string,                  // original placement of selected content for nielsen tracking
        bucketId: PropTypes.number,
        bucketName: PropTypes.string,
        pageId: PropTypes.string,
        pageName: PropTypes.string,
        player: PropTypes.object,                     // from selector, player state
        playerTech: PropTypes.string,
        autoPlay: PropTypes.bool,
        dssPlayerMuted: PropTypes.bool,
        selectedContent: PropTypes.object,            // from selector, currently selected content
        selectedStream: PropTypes.object,              // from selector, currently selected stream
        playingStream: PropTypes.object,              // from selector, currently playing stream
        provider: PropTypes.object,                   // from selector, provider to pass to Conviva
        routing: PropTypes.object,                    // mapped from react-redux-router
        scoringComplete: PropTypes.bool,              // from selector, indicates scoring status
        userAuth: PropTypes.array,                    // from selector
        preauthorizedResources: PropTypes.array,
        userInitiated: PropTypes.bool,                // from router location "state", used for analytics
        oneIdToken: PropTypes.object,
        bypassToken: PropTypes.string,                // eslint-disable-line react/no-unused-prop-types
        isDomestic: PropTypes.bool,
        intlApp: PropTypes.bool,
        recommendMobileWeb: PropTypes.bool,            // eslint-disable-line react/no-unused-prop-types
        flags: PropTypes.object,
        location: PropTypes.object,
        playbackMeta: PropTypes.object,
        prerollConfig: PropTypes.object,
        playerAdsConfig: PropTypes.object,
        device: PropTypes.string,
        edition: PropTypes.string,
        pickerStreams: PropTypes.array,
        country: PropTypes.string,
        lastFeeds: PropTypes.object,
        appName: PropTypes.string,
        hideNav: PropTypes.bool,
        shieldApiKey: PropTypes.string,
        playerOpts: PropTypes.object
    };

    ESPNPlayer.defaultProps = {
        disableAuthPrompt: true,
        initialAutoEmbedCountDown: 2,
        initialEmbedContent: {},
        recommendMobileWeb: false,
        userAuth: [],
        oneIdToken: {}
    };

    const mapStateToProps = (state) => {
        const {
            app: { isMobile = false, env, lang, country, device, openPicker, appName },
            player,
            routing
        } = state;
        const page = pageSelector(state);
        const edition = editionSelector(state).edition;
        const shieldApiKey = shieldApiKeySelector(state, edition);
        const playerOpts = playerOptionsSelector(state);

        return {
            bootstrap: bootstrapSelector(state),
            apiEnv: apiEnvSelector(state),
            env,
            lang,
            country,
            initialEmbedContent: initialEmbedSelector(state),
            isMobile,
            device,
            openPicker,
            nielsenTrackingData: nielsenTrackingSelector(state),
            comscoreTrackingData: comscoreTrackingSelector(state),
            onDeckContent: onDeckContentSelector(state),
            player,
            playerTech: playerTechSelector(state),
            autoPlay: autoPlaySelector(state),
            dssPlayerMuted: dssPlayerMutedSelector(state),
            selectedContent: selectedContentSelector(state),
            selectedStream: selectedStreamSelector(state),
            playingStream: playingStreamSelector(state),
            provider: providerSelector(state),
            routing,
            scoringComplete: page.scored,
            userAuth: userAuthSelector(state),
            preauthorizedResources: preauthorizedResourcesSelector(state),
            oneIdToken: oneIdTokenSelector(state),
            passOneIdToken: passOneIdTokenSelector(state),
            bypassToken: bypassTokenSelector(state),
            isDomestic: isDomesticSelector(state),
            edition,
            intlApp: intlAppSelector(state),
            recommendMobileWeb: recommendMobileWebSelector(state),
            flags: persistentFlagsSelector(state),
            location: locationSelector(state),
            playbackMeta: playbackMetaSelector(state),
            prerollConfig: prerollConfigSelector(state),
            lastFeeds: lastFeedsSelector(state),
            playerAdsConfig: playerAdsConfigSelector(state),
            oneId: state.oneId,
            newrelicInsertKey: state.app.newrelicInsertKey,
            newrelicAccountId: state.app.newrelicAccountId,
            isNewrelicTrackingOn: newRelicTrackingSelector(state),
            pickerStreams: pickerStreamsSelector(state),
            appName,
            hideNav: hideNavSelector(state),
            shieldApiKey,
            playerOpts
        };
    };

    return connect(
        mapStateToProps,
        null,
        null,
        { withRef: true }
    )(injectIntl(hoistStatics(ESPNPlayer, WrappedComponent), { withRef: true }));
}

export default connectPlayer;



// WEBPACK FOOTER //
// ./app/webapp/player/connectPlayer.js